import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Installation } from "data/api";
import { containsQuery } from "src/util";

import { Loading } from "components/Loading";
import { Pill } from "components/Pill";
import SearchableTable from "components/SearchableTable";
import { TableComponent } from "components/TableComponent";
import { Monospace } from "components/Typography/Monospace";

import { useListAllInstallations } from "./useListAllInstallations";

const LoadingTable = () => (
  <div className="flex justify-center">
    <Loading />
  </div>
);

type GroupedInstallations = {
  groupRef: string;
  groupName: string;
  installations: Installation[];
};

const customersColumns = [
  { name: "Customer Name" },
  { name: "Group Ref" },
  { name: "Total Installations" },
  { name: "Healthy Installations" },
];

const customersSearchFunction = (
  query: string,
  group: GroupedInstallations
) => {
  if (!group) return false;

  const { groupRef, groupName } = group;
  const integrationNames = group.installations
    .map((i) => i.integrationId)
    .join(" ");

  return containsQuery(query, groupRef, groupName, integrationNames);
};

const CustomerRow = ({ group }: { group: GroupedInstallations }) => {
  const healthyCount = group.installations.filter(
    (i) => i.healthStatus === "healthy"
  ).length;
  const totalCount = group.installations.length;
  const status = healthyCount === totalCount ? "healthy" : "unhealthy";
  const { projectId } = useParams();

  return (
    <TableComponent.TableRow>
      <TableComponent.TableCell>
        <Link
          className="text-slate-900 underline"
          to={`/projects/${projectId}/customers/${group.groupRef}`}
        >
          {group.groupName}
        </Link>
      </TableComponent.TableCell>
      <TableComponent.TableCell>
        <Monospace>{group.groupRef}</Monospace>
      </TableComponent.TableCell>
      <TableComponent.TableCell>
        <Monospace>{group.installations.length}</Monospace>
      </TableComponent.TableCell>
      <TableComponent.TableCell>
        <Pill
          color={status === "healthy" ? "green" : "red"}
          text={`${healthyCount} / ${totalCount} Healthy`}
        />
      </TableComponent.TableCell>
    </TableComponent.TableRow>
  );
};

export const CustomersTable = () => {
  const { data: installations, isLoading, isError } = useListAllInstallations();

  const groupedInstallations = useMemo(() => {
    if (!installations) return [];

    const groups = installations.reduce(
      (acc: Record<string, GroupedInstallations>, installation) => {
        const groupRef = installation.group?.groupRef;
        if (!groupRef) return acc;

        if (!acc[groupRef]) {
          acc[groupRef] = {
            groupRef,
            groupName: installation.group?.groupName || groupRef,
            installations: [],
          };
        }

        acc[groupRef].installations.push(installation);
        return acc;
      },
      {}
    );

    return Object.values(groups);
  }, [installations]);

  if (isError) {
    return <p className="p-4">There was an error loading the customers.</p>;
  }

  if (isLoading) {
    return <LoadingTable />;
  }

  if (!groupedInstallations.length) {
    return (
      <div className="p-4 text-center text-gray-600">No customers found.</div>
    );
  }

  return (
    <SearchableTable
      initialData={groupedInstallations}
      searchFunction={customersSearchFunction}
      rowComponent={(group: GroupedInstallations) => (
        <CustomerRow key={group.groupRef} group={group} />
      )}
      columns={customersColumns}
    />
  );
};
